<script lang="ts" setup>
import type { DataLayerItem } from '~layers/composables/useDataLayer'
import type { BannerTypeFragment, ShopTheLookTypeFragment } from '#graphql-operations'

import { StrapiBanner, LookTile } from '#components'

const { setBreadcrumbs } = useBreadcrumbs()
const { search } = useCatalogSearch()
const route = useRoute()
const { $t } = useI18n()

const meta = inject('meta', ref())

setBreadcrumbs({
  label: meta.value.label,
  parents: meta.value.parents,
})

const {
  products,
  fetchProductData,
} = useCatalog(meta)

const {
  page,
  totalPages,
  sort,
  sortOptions,
  view,
  totalCount,
  paginatorMethods,
} = useCatalogPaginator()

const {
  filters,
} = useCatalogFilters(meta)

const { strapiData, getStrapiData } = useStrapiCms<StrapiPage>(
  meta.value?.strapi_id || '',
  'PUBLISHED',
  'plpPage',
  'page',
  meta.value?.strapi_locale || '',
)

await Promise.all([
  fetchProductData(),
  meta?.value?.strapi_id ? getStrapiData() : undefined,
])

const plpPage = computed(() => {
  return strapiData.value
})

const pageTitle = computed(
  () => meta.value?.label,
)

const metaTitle = computed(
  () => plpPage.value?.MetaData?.MetaTitle || pageTitle.value + ' | ' + $t('meta.title'),
)

const metaDescription = computed(
  () => plpPage.value?.MetaData?.MetaDescription || $t('meta.plp.description', { name: pageTitle.value }),
)

const plpUsps = computed(() => {
  return plpPage.value?.PLPData?.Usp || strapiData.value?.plpUsps?.[0]
})

const plpTooltips = computed(() => {
  return strapiData.value?.tooltips
})

provide('tooltips', plpTooltips)

const plpBanner = computed(() => {
  if (!strapiData.value?.Image) return null

  const imageData = strapiData.value.Image
  const imageMobileData = strapiData.value?.ImageMobile

  return {
    ButtonText: undefined,
    ButtonType: undefined,
    File: {
      Media: {
        url: imageData.url,
        width: imageData.width,
        height: imageData.height,
        mime: imageData.mime,
        alternativeText: imageData?.alternativeText,
      },
      MediaMobile: imageMobileData
        ? {
            url: imageMobileData.url,
            width: imageMobileData.width,
            height: imageMobileData.height,
            mime: imageMobileData.mime,
          }
        : undefined,
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any as BannerTypeFragment
})

const plpBanners = computed(() => {
  return plpPage.value?.PLPData?.Banners
})

const plpQuicklinks = computed(() => {
  return plpPage.value?.PLPData?.Quicklinks
})

const plpGridContent = computed(() => {
  return [
    ...(plpPage.value?.PLPData?.GridBanners || []),
  ]
})

const plpGridPosition1 = computed(() => {
  return plpGridContent.value?.[0]
})

const plpGridPosition2 = computed(() => {
  return plpPage.value?.PLPData?.GridLooks[0] || plpGridContent.value?.[1]
})

const { pageView, viewItemList } = useDataLayer()
pageView('overview', pageTitle.value)

const dataLayerOverrides = computed<Partial<DataLayerItem>>(() => ({
  item_list_name: 'plp',
  item_list_id: route.fullPath,
}))

const category = computed<Partial<MagentoCategory>>(() => ({
  id: meta.value?.id?.split('-')?.[1],
  name: meta.value?.label,
}))

watch(products, () => {
  viewItemList(products.value, dataLayerOverrides.value, category.value)
}, { immediate: true })

const metaRobots = computed(() => {
  const { p, filter, color, ...restOfQuery } = route.query
  if (Object.keys(restOfQuery).length > 0) {
    return 'noindex, follow'
  }
  if (plpPage.value?.MetaData?.MetaRobots) {
    return plpPage.value.MetaData?.MetaRobots.replace('_', ', ')
  }
  return 'index, follow'
})

useHead({
  title: metaTitle,
  meta: [
    { key: 'description', name: 'description', content: metaDescription },
    { key: 'robots', name: 'robots', content: metaRobots },
  ],
})

const canonicalUrl = computed(() => {
  if (metaRobots.value.startsWith('index')) {
    return route.fullPath
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { filter, color, ...restOfQuery } = route.query
  const searchParams = new URLSearchParams({
    ...(color ? { filter, color } : {}),
  })
  return route.path + (searchParams.size > 0 ? '?' + searchParams.toString() : '')
})

const { canonical } = useSeo()
canonical(canonicalUrl)

const getComponent = (component: BannerTypeFragment | ShopTheLookTypeFragment | undefined) => {
  if (!component) return null
  if (component.__typename === 'Banner') return StrapiBanner
  if (component.__typename === 'ShopTheLook') return LookTile
  return null
}

const getComponentProps = (component: BannerTypeFragment | ShopTheLookTypeFragment | undefined) => {
  if (!component) return {}
  if (component.__typename === 'Banner') return { banner: component }
  if (component.__typename === 'ShopTheLook') return { look: component }
  return {}
}
</script>

<template>
  <div class="plp">
    <StrapiBanner
      v-if="plpBanner"
      class="plp__hero-banner banner--hero"
      :banner="plpBanner"
      :lazy="false"
    />

    <AppBreadcrumbs />

    <div class="plp__container">
      <div class="plp__layout">
        <div class="plp__intro">
          <!-- <AppLink
            v-if="previousPage"
            :to="`/${previousPage.url}`"
            class="plp__back"
          >
            <NuxtIcon name="icon:arrow" />
            {{ previousPage.label }}
          </AppLink> -->

          <UiHeading
            force-tag="h1"
            class="plp__heading"
          >
            <span v-if="search">{{ $t('Search results for') }}: '{{ search }}'</span>
            <span v-else>{{ pageTitle }}</span>
          </UiHeading>

          <UiHeadingSection>
            <CatalogBanners
              v-if="plpBanners?.length"
              class="plp__banners"
              :banners="plpBanners"
            />

            <!-- <div
              v-if="plpBanners?.length"
              class="plp__banners"
            >
              <StrapiBanner
                v-for="(banner, index) in plpBanners"
                :key="index"
                class="plp__banner"
                :banner="banner"
                :lazy="false"
              />
            </div> -->

            <CatalogQuickLinks
              v-if="plpQuicklinks?.length"
              class="plp__quick-links"
              :links="plpQuicklinks"
            />
          </UiHeadingSection>
        </div>

        <UiHeadingSection>
          <div class="plp__sidebar">
            <!-- <FilterCategories class="plp__categories" :url-data="urlData" /> -->
            <FilterList
              class="plp__filters"
              :filters="filters"
            />
          </div>

          <PaginatorToolbar
            class="plp__paginator plp__paginator--top"
            :total-count="totalCount"
            :view="undefined"
            :sort="sort"
            :sort-options="sortOptions"
            v-on="paginatorMethods"
          />

          <div
            id="product-grid"
            class="plp__products products"
            :class="`products--${view}`"
          >
            <ProductTile
              v-for="(product, index) in products"
              :key="index"
              :data-layer-overrides="dataLayerOverrides"
              :product="product"
              list="ProductListerPage"
            />

            <AppUsps
              v-if="plpUsps && page === 1 && products.length >= 3"
              class="plp__usps"
              :usps="plpUsps"
              :horizontal="true"
              :slider="true"
            />

            <component
              :is="getComponent(plpGridPosition1)"
              v-if="plpGridPosition1 && page === 1 && products.length >= 4"
              class="plp__grid-element plp__grid-element--1"
              v-bind="getComponentProps(plpGridPosition1)"
              :lazy="true"
            />

            <component
              :is="getComponent(plpGridPosition2)"
              v-if="plpGridPosition2 && page === 1 && products.length >= 6"
              class="plp__grid-element plp__grid-element--2"
              v-bind="getComponentProps(plpGridPosition2)"
              :lazy="true"
            />
          </div>

          <PaginatorToolbar
            class="plp__paginator plp__paginator--bottom"
            :current-page="page"
            :total-pages="totalPages"
            :items-per-page="undefined"
            :view="undefined"
            v-on="paginatorMethods"
          />
        </UiHeadingSection>
      </div>
    </div>

    <UiHeadingSection>
      <StrapiSlices
        v-if="plpPage?.Slices?.length"
        class="plp__content"
        :slices="plpPage.Slices"
      />
    </UiHeadingSection>

    <!-- <UiScrollToTop class="plp__scroll-to-top" /> -->

    <!--  -->
    <!--  -->
    <!--  -->
    <!--  -->
    <!--  -->
    <!--  -->
    <!--  -->
    <!--  -->
    <!--  -->
  </div>
</template>

<style>
@import url("~layers/pages/~plp.css");
</style>
